import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
    return (
        <div className="privacy-page">
            <div className="privacy-container">
                {/* Header */}
                <header className="privacy-header">
                    <Link to="/login" className="privacy-logo">
                        <img src="/logo.png" alt="Blastmate" />
                        <span>Blastmate</span>
                    </Link>
                    <h1>Privacy Policy</h1>
                    <p className="privacy-effective">
                        <strong>Effective Date:</strong> 1 March 2026 &nbsp;|&nbsp;
                        <strong>Last Updated:</strong> 6 March 2026
                    </p>
                </header>

                {/* Content */}
                <div className="privacy-content">
                    {/* 1. Introduction */}
                    <section className="privacy-section">
                        <h2>1. Introduction</h2>
                        <p>
                            Welcome to <strong>Blastmate</strong>, a product of <strong>Root Source Solutions Pvt Ltd</strong> ("we", "us", "our").
                            Blastmate is a multi-tenant Software-as-a-Service (SaaS) platform that enables businesses
                            to manage WhatsApp Business communications, including bulk campaigns, automated responses,
                            AI-powered customer interactions, and customer relationship management.
                        </p>
                        <p>
                            This Privacy Policy explains how we collect, use, store, share, and protect information
                            when you use our platform at <a href="https://blastmate.in" target="_blank" rel="noopener noreferrer">blastmate.in</a>,
                            our APIs, and related services (collectively, the "Service").
                        </p>
                        <p>
                            By accessing or using Blastmate, you agree to the practices described in this Privacy Policy.
                            If you do not agree, please do not use our Service.
                        </p>
                    </section>

                    {/* 2. Information We Collect */}
                    <section className="privacy-section">
                        <h2>2. Information We Collect</h2>

                        <h3>2.1 Account Information</h3>
                        <p>When you create a Blastmate account, we collect:</p>
                        <ul>
                            <li>Business name</li>
                            <li>Email address</li>
                            <li>Phone number</li>
                            <li>Password (stored as a bcrypt hash — we never store plain-text passwords)</li>
                        </ul>

                        <h3>2.2 WhatsApp Business Data</h3>
                        <p>When you connect your WhatsApp Business account via Meta's OAuth flow, we receive and store:</p>
                        <ul>
                            <li>WhatsApp Business Account ID (WABA ID)</li>
                            <li>Phone Number ID</li>
                            <li>Display phone number</li>
                            <li>OAuth access tokens (encrypted at rest using Fernet symmetric encryption)</li>
                        </ul>

                        <h3>2.3 Message Data</h3>
                        <p>Through the WhatsApp Cloud API, we process and store:</p>
                        <ul>
                            <li>Inbound messages from your customers (text, images, documents)</li>
                            <li>Outbound messages you send (manual replies, campaigns, bot/AI responses)</li>
                            <li>Message delivery status (sent, delivered, read, failed)</li>
                            <li>Message metadata (timestamps, message IDs)</li>
                        </ul>

                        <h3>2.4 Customer Contact Data</h3>
                        <p>You may upload or accumulate customer contact data, including:</p>
                        <ul>
                            <li>Customer phone numbers</li>
                            <li>Customer names (from WhatsApp profiles)</li>
                            <li>Tags and segmentation labels you assign</li>
                            <li>Conversation history and interaction statistics</li>
                        </ul>

                        <h3>2.5 Knowledge Base Content</h3>
                        <p>If you use our AI features, you may upload:</p>
                        <ul>
                            <li>Business information (menus, pricing, FAQs, policies)</li>
                            <li>PDF documents and images for AI training</li>
                        </ul>

                        <h3>2.6 Payment Information</h3>
                        <p>
                            Payment processing is handled by <strong>Razorpay</strong>. We do not store your full
                            credit card or bank account details. We store:
                        </p>
                        <ul>
                            <li>Razorpay customer ID and subscription ID</li>
                            <li>Payment method type (UPI, card, net banking)</li>
                            <li>Payment history (amounts, dates, status)</li>
                        </ul>

                        <h3>2.7 Usage and Analytics Data</h3>
                        <ul>
                            <li>Messages sent/received counts</li>
                            <li>Campaign delivery statistics</li>
                            <li>AI token usage</li>
                            <li>Login timestamps and activity logs</li>
                        </ul>
                    </section>

                    {/* 3. How We Use Your Information */}
                    <section className="privacy-section">
                        <h2>3. How We Use Your Information</h2>
                        <p>We use the collected information to:</p>
                        <ul>
                            <li><strong>Provide the Service:</strong> Send and receive WhatsApp messages on your behalf, manage campaigns, power automated bot and AI responses</li>
                            <li><strong>Process Payments:</strong> Manage subscriptions, billing, and usage metering</li>
                            <li><strong>Improve the Platform:</strong> Analyze usage patterns to enhance features, performance, and reliability</li>
                            <li><strong>Communicate with You:</strong> Send service-related notifications, usage warnings (e.g., at 80% message limit), and billing reminders</li>
                            <li><strong>Ensure Security:</strong> Detect and prevent unauthorized access, fraud, and abuse</li>
                            <li><strong>Comply with Legal Obligations:</strong> Respond to lawful requests from authorities where required</li>
                        </ul>
                    </section>

                    {/* 4. WhatsApp & Meta Data Handling */}
                    <section className="privacy-section">
                        <h2>4. WhatsApp & Meta Platform Data Handling</h2>
                        <p>
                            Blastmate integrates with the <strong>Meta WhatsApp Cloud API</strong> as a registered
                            Technology Provider. This section specifically addresses how we handle data received
                            from Meta's platform.
                        </p>

                        <h3>4.1 Data Received from Meta</h3>
                        <ul>
                            <li>Webhook notifications containing incoming messages, delivery statuses, and account events</li>
                            <li>OAuth tokens for WhatsApp Business Account access</li>
                            <li>WhatsApp Business Account metadata (WABA ID, phone number ID)</li>
                        </ul>

                        <h3>4.2 How We Use Meta Data</h3>
                        <p>Data received from Meta is used exclusively to:</p>
                        <ul>
                            <li>Display incoming messages to business users in the Blastmate dashboard</li>
                            <li>Send outbound messages (replies, campaigns, bot/AI responses) via the WhatsApp Cloud API</li>
                            <li>Track message delivery status</li>
                            <li>Manage WhatsApp Business Account connections</li>
                        </ul>

                        <h3>4.3 Meta's Role</h3>
                        <p>
                            For certain metadata and log data, WhatsApp (Meta) acts as a <strong>data controller</strong>.
                            We inform our business users that Meta processes this data in accordance with
                            Meta's own <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">WhatsApp Privacy Policy</a>.
                            We do not use data received from Meta for any purpose other than providing
                            WhatsApp messaging services through the Blastmate platform.
                        </p>

                        <h3>4.4 OAuth Token Security</h3>
                        <p>
                            All WhatsApp access tokens are encrypted at rest using industry-standard
                            <strong> Fernet symmetric encryption</strong> and are never exposed in logs, APIs, or to
                            unauthorized personnel.
                        </p>
                    </section>

                    {/* 5. Data Sharing & Third Parties */}
                    <section className="privacy-section">
                        <h2>5. Data Sharing & Third Parties</h2>
                        <p>
                            We do not sell, rent, or trade your personal data to third parties. We share data only
                            with the following service providers who are essential to operating the platform:
                        </p>

                        <div className="privacy-table-wrap">
                            <table className="privacy-table">
                                <thead>
                                    <tr>
                                        <th>Provider</th>
                                        <th>Purpose</th>
                                        <th>Data Shared</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Meta (WhatsApp Cloud API)</td>
                                        <td>WhatsApp messaging</td>
                                        <td>Messages, phone numbers, account IDs</td>
                                    </tr>
                                    <tr>
                                        <td>Razorpay</td>
                                        <td>Payment processing</td>
                                        <td>Payment details, subscription info</td>
                                    </tr>
                                    <tr>
                                        <td>MongoDB Atlas</td>
                                        <td>Database hosting</td>
                                        <td>All application data (encrypted in transit)</td>
                                    </tr>
                                    <tr>
                                        <td>DigitalOcean</td>
                                        <td>Application hosting</td>
                                        <td>Application runtime data</td>
                                    </tr>
                                    <tr>
                                        <td>AI Provider (Internal)</td>
                                        <td>AI-powered responses</td>
                                        <td>Anonymized query context from knowledge base</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <p>
                            Each third-party provider is bound by their own privacy policies and data processing
                            agreements. We only share the minimum data necessary for each provider to perform their function.
                        </p>
                    </section>

                    {/* 6. Data Retention & Deletion */}
                    <section className="privacy-section">
                        <h2>6. Data Retention & Deletion</h2>

                        <h3>6.1 Retention Periods</h3>
                        <ul>
                            <li><strong>Account data:</strong> Retained for the duration of your active subscription, plus 90 days after cancellation</li>
                            <li><strong>Message data:</strong> Retained for up to 12 months from the date of the message</li>
                            <li><strong>Payment records:</strong> Retained for 7 years as required by Indian tax and accounting regulations</li>
                            <li><strong>Audit logs:</strong> Retained for 12 months for security and compliance purposes</li>
                        </ul>

                        <h3>6.2 Data Isolation</h3>
                        <p>
                            Blastmate uses per-client data isolation. Each business account's data (messages, customers,
                            knowledge base, bot flows) is stored in dedicated, isolated database collections. No
                            client can access another client's data.
                        </p>

                        <h3>6.3 Data Deletion Requests</h3>
                        <p>You may request deletion of your data at any time by:</p>
                        <ul>
                            <li>Emailing us at <a href="mailto:privacy@blastmate.in">privacy@blastmate.in</a></li>
                            <li>Using the account deletion option in Settings (when available)</li>
                        </ul>
                        <p>
                            Upon receiving a valid deletion request, we will delete all your personal data and
                            client-specific collections within <strong>30 days</strong>, except where retention is
                            required by law (e.g., payment records).
                        </p>
                    </section>

                    {/* 7. User Consent & Opt-In */}
                    <section className="privacy-section">
                        <h2>7. User Consent & Opt-In for WhatsApp Messaging</h2>
                        <p>
                            Blastmate requires that all businesses using our platform obtain <strong>explicit,
                                verifiable opt-in consent</strong> from their end-customers before sending them WhatsApp messages.
                        </p>
                        <ul>
                            <li>Businesses must collect consent through their own channels (website forms, in-store sign-ups, etc.)</li>
                            <li>Pre-checked consent boxes are not acceptable</li>
                            <li>End-customers must be able to opt out at any time by replying "STOP" or through the business's opt-out mechanism</li>
                            <li>Businesses are responsible for maintaining records of consent</li>
                        </ul>
                        <p>
                            We enforce Meta's messaging policies, including template approval requirements and
                            quality score monitoring, to ensure compliant messaging practices.
                        </p>
                    </section>

                    {/* 8. Data Security */}
                    <section className="privacy-section">
                        <h2>8. Data Security</h2>
                        <p>We implement industry-standard security measures to protect your data:</p>
                        <ul>
                            <li><strong>Encryption in Transit:</strong> All data is transmitted over HTTPS/TLS</li>
                            <li><strong>Encryption at Rest:</strong> Sensitive credentials (OAuth tokens, API keys) are encrypted using Fernet symmetric encryption</li>
                            <li><strong>Password Security:</strong> User passwords are hashed using bcrypt with salt</li>
                            <li><strong>Authentication:</strong> JWT-based authentication with secure token management</li>
                            <li><strong>Multi-Tenant Isolation:</strong> Strict per-client data isolation at the database level</li>
                            <li><strong>Access Control:</strong> Role-based access control separating admin and client permissions</li>
                            <li><strong>Webhook Verification:</strong> Razorpay webhooks are verified using signature validation</li>
                        </ul>
                    </section>

                    {/* 9. Cookies & Local Storage */}
                    <section className="privacy-section">
                        <h2>9. Cookies & Local Storage</h2>
                        <p>Blastmate uses minimal browser storage:</p>
                        <ul>
                            <li><strong>Authentication Token:</strong> A JWT token is stored in localStorage to maintain your login session</li>
                            <li><strong>Role Information:</strong> Your account role (admin/client) is stored in localStorage for navigation purposes</li>
                        </ul>
                        <p>
                            We do not use third-party tracking cookies, advertising pixels, or analytics
                            trackers. We do not track you across other websites.
                        </p>
                    </section>

                    {/* 10. Children's Privacy */}
                    <section className="privacy-section">
                        <h2>10. Children's Privacy</h2>
                        <p>
                            Blastmate is a business-to-business platform designed for use by businesses and their
                            authorized representatives. Our Service is not intended for individuals under the age of 18.
                            We do not knowingly collect personal information from children. If we become aware that
                            a child under 18 has provided us with personal data, we will take steps to delete
                            such information promptly.
                        </p>
                    </section>

                    {/* 11. Your Rights */}
                    <section className="privacy-section">
                        <h2>11. Your Rights</h2>
                        <p>Depending on your jurisdiction, you may have the following rights:</p>
                        <ul>
                            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                            <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                            <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
                            <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
                            <li><strong>Objection:</strong> Object to certain types of data processing</li>
                            <li><strong>Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
                        </ul>
                        <p>
                            To exercise any of these rights, please contact us at{' '}
                            <a href="mailto:privacy@blastmate.in">privacy@blastmate.in</a>.
                            We will respond to valid requests within <strong>30 days</strong>.
                        </p>
                    </section>

                    {/* 12. Changes to This Policy */}
                    <section className="privacy-section">
                        <h2>12. Changes to This Privacy Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time to reflect changes in our practices,
                            technology, legal requirements, or other factors. When we make material changes, we will:
                        </p>
                        <ul>
                            <li>Update the "Last Updated" date at the top of this page</li>
                            <li>Notify registered users via email for significant changes</li>
                            <li>Display a notice on the platform dashboard</li>
                        </ul>
                        <p>
                            Your continued use of Blastmate after any changes constitutes your acceptance of the
                            updated Privacy Policy.
                        </p>
                    </section>

                    {/* 13. Contact Us */}
                    <section className="privacy-section">
                        <h2>13. Contact Us</h2>
                        <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
                        <div className="privacy-contact-card">
                            <p><strong>Root Source Solutions Pvt Ltd</strong></p>
                            <p>Operating as Blastmate</p>
                            <p>Email: <a href="mailto:privacy@blastmate.in">privacy@blastmate.in</a></p>
                            <p>Website: <a href="https://blastmate.in" target="_blank" rel="noopener noreferrer">blastmate.in</a></p>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <footer className="privacy-footer">
                    <p>&copy; {new Date().getFullYear()} Root Source Solutions Pvt Ltd. All rights reserved.</p>
                    <div className="privacy-footer-links">
                        <Link to="/login">Sign In</Link>
                        <span className="privacy-footer-sep">·</span>
                        <Link to="/signup">Create Account</Link>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
